import { defineStore } from 'pinia'
import {
  canEvolveByItem,
  pokemonXpForLevel,
  getEvoStageMult,
  EVOLUTIONS,
  getLevelEvolutionsCrossed,
  getApplicableLevelEvolutionsAtLevel,
  pickLevelEvolutionForLivingDex,
} from '~/data/evolutions'
import type { Evolution } from '~/data/evolutions'
import { getRarity, hasKnownRarity } from '~/data/gacha'
import type { Rarity } from '~/data/gacha'
import { DISPLAY_RARITIES, getDisplayRarity, getTeamPower, resolvePowerBaseSlug } from '~/data/pokemonPower'
import type { DisplayRarity } from '~/data/pokemonPower'
import { getGenForSlug, POKEDEX } from '~/data/pokedex'
import { TYPES } from '~/data/types'
import type { PokemonType } from '~/data/types'
import { useDaycareStore } from '~/stores/useDaycareStore'
import { useGmaxRaidStore } from '~/stores/useGmaxRaidStore'
import { isGmaxFormSlug, isGmaxFormUnlocked, isPokemonFormUnlocked } from '~/utils/pokemonFormUnlock'
import { resolveCanonicalSpeciesNames } from '~/utils/speciesLocalizedLabel'

const VALID_TYPE_IDS = new Set<string>(TYPES.map((t) => t.id))

const VALID_RARITY_IDS = new Set<DisplayRarity>(DISPLAY_RARITIES)

function normalizeFilterRarities(saved: Record<string, unknown>): DisplayRarity[] {
  const raw = saved.filterRarities
  if (!Array.isArray(raw)) return []
  const out: DisplayRarity[] = []
  for (const r of raw) {
    if (typeof r === 'string' && VALID_RARITY_IDS.has(r as DisplayRarity)) out.push(r as DisplayRarity)
  }
  return out
}

function normalizeFilterStars(saved: Record<string, unknown>): number[] {
  const raw = saved.filterStars
  if (!Array.isArray(raw)) return []
  const out: number[] = []
  for (const s of raw) {
    const n = Number(s)
    if (Number.isInteger(n) && n >= 1 && n <= 5) out.push(n)
  }
  return out
}

function normalizeFilterTypes(saved: Record<string, unknown>): PokemonType[] {
  const raw = saved.filterTypes
  if (Array.isArray(raw)) {
    const out: PokemonType[] = []
    for (const t of raw) {
      if (typeof t === 'string' && VALID_TYPE_IDS.has(t)) out.push(t as PokemonType)
    }
    return out
  }
  const single = saved.filterType
  if (typeof single === 'string' && VALID_TYPE_IDS.has(single)) return [single as PokemonType]
  return []
}

const FILTERS_KEY = 'poke-idle-inventory-filters'

function loadFiltersFromStorage() {
  try {
    const raw = localStorage.getItem(FILTERS_KEY)
    if (raw) return JSON.parse(raw)
  } catch { /* ignore */ }
  return {}
}

function saveFiltersToStorage(filters: Record<string, unknown>) {
  try {
    localStorage.setItem(FILTERS_KEY, JSON.stringify(filters))
  } catch { /* ignore */ }
}

export interface OwnedPokemon {
  id: number
  serverId: number | null
  slug: string
  nameFr: string
  nameEn: string
  level: number
  xp: number
  stars: number
  isShiny: boolean
  rarity: Rarity
  teamSlot: number | null
  isFavorite?: boolean
  cosmeticFormSlug?: string | null
  hasEvolved?: boolean
}

export interface AddPokemonInput {
  slug: string
  nameFr: string
  nameEn: string
  stars: number
  isShiny: boolean
  rarity: Rarity
  cosmeticFormSlug?: string | null
  hasEvolved?: boolean
}

export interface SavedTeam {
  id: string
  name: string
  pokemonIds: number[]
  tags?: string[]
  note?: string
  pinned?: boolean
  createdAt?: number
  updatedAt?: number
  lastUsedMode?: string | null
}

export interface EvolutionEvent {
  fromNameFr: string
  fromNameEn: string
  toNameFr: string
  toNameEn: string
  wasInTeam: boolean
  slot: number | null
  /** Si défini, le layout utilise ce texte pour le toast au lieu du modèle « X a évolué en Y ». */
  toastFr?: string
  toastEn?: string
}

interface InventoryState {
  collection: OwnedPokemon[]
  nextId: number
  savedTeams: SavedTeam[]
  evolutionLog: EvolutionEvent[]
  // UI filter state (persists across page navigations, not saved to server)
  filterSortBy: 'stars' | 'level' | 'name' | 'dps' | 'pokedex' | 'rarity' | 'totalMult' | 'favorite'
  filterSearch: string
  /** Pokémon shown if they have any of these types (OR). Empty = no type filter. */
  filterTypes: PokemonType[]
  /** Pokémon shown if their display rarity is in this set (OR). Empty = no rarity filter. */
  filterRarities: DisplayRarity[]
  /** Pokémon shown if their stars are in this set (OR). Empty = no stars filter. */
  filterStars: number[]
  filterShiny: boolean | null
  /** true = uniquement les espèces shiny jamais consultées (badge NEW). */
  filterNewShiny: boolean
  filterTeam: boolean | null
  /** true = favorites only, null = no favorites filter */
  filterFavorites: boolean | null
  filterEvoStage: number | null
  filterGen: number | null
  filterEvolvable: boolean | null
  /** true = uniquement les espèces dont la forme Gigamax se débloque via les antres. */
  filterGmaxDen: boolean
  /** Si true, masque les Pokémon au niveau max (100) dans la grille. */
  filterHideLevel100: boolean
  showEvoNotifs: boolean
}

export const MAX_STARS = 5
export const MAX_LEVEL = 100

// Module-level dirty flag — tracks whether pokemon collection changed since last save
// Kept outside Pinia state to avoid triggering reactive watchers
let _pokemonsDirtyGen = 0
let _pokemonsSavedGen = 0
export function isPokemonsDirty() { return _pokemonsDirtyGen > _pokemonsSavedGen }
export function resetPokemonsDirty(savedGen?: number) {
  if (savedGen !== undefined) {
    _pokemonsSavedGen = Math.max(_pokemonsSavedGen, savedGen)
  } else {
    _pokemonsSavedGen = _pokemonsDirtyGen
  }
}
export function markPokemonsDirty() { _pokemonsDirtyGen++; _evolutionCheckNeeded = true }
export function snapshotPokemonsDirtyGen() { return _pokemonsDirtyGen }

// Dirty flag for checkAllEvolutions — avoids O(n²) scan on every route change
// when nothing has changed. Reset to true whenever the collection mutates.
let _evolutionCheckNeeded = true
export function markEvolutionCheckNeeded() { _evolutionCheckNeeded = true }
export function consumeEvolutionCheckNeeded(): boolean {
  if (!_evolutionCheckNeeded) return false
  _evolutionCheckNeeded = false
  return true
}

// Delta-save tracking: which pokemon (by clientId) changed since the last save.
// On save we send only dirty pokemon; the server handles partial upserts.
const _dirtyPokemonIds = new Set<number>()
export function markPokemonIdDirty(id: number) { _dirtyPokemonIds.add(id) }
export function getDirtyPokemonIds(): ReadonlySet<number> { return _dirtyPokemonIds }
export function clearDirtyPokemonIds() { _dirtyPokemonIds.clear() }

let _savedTeamsDirtyGen = 0
let _savedTeamsSavedGen = 0
export function isSavedTeamsDirty() {
  return _savedTeamsDirtyGen > _savedTeamsSavedGen
}
export function markSavedTeamsDirty() {
  _savedTeamsDirtyGen++
}
export function resetSavedTeamsDirty(savedGen?: number) {
  if (savedGen !== undefined) {
    _savedTeamsSavedGen = Math.max(_savedTeamsSavedGen, savedGen)
  } else {
    _savedTeamsSavedGen = _savedTeamsDirtyGen
  }
}

export const useInventoryStore = defineStore('inventory', {
  state: (): InventoryState => {
    const saved = typeof localStorage !== 'undefined' ? loadFiltersFromStorage() : {}
    return {
      collection: [],
      nextId: 1,
      savedTeams: [],
      evolutionLog: [],
      filterSortBy: (['stars', 'level', 'name', 'dps', 'pokedex', 'rarity', 'totalMult', 'favorite'] as const).includes(
        saved.filterSortBy,
      )
        ? saved.filterSortBy
        : 'stars',
      filterSearch: saved.filterSearch ?? '',
      filterTypes: normalizeFilterTypes(saved),
      filterRarities: normalizeFilterRarities(saved),
      filterStars: normalizeFilterStars(saved),
      filterShiny: saved.filterShiny ?? null,
      filterNewShiny: saved.filterNewShiny === true,
      filterTeam: saved.filterTeam ?? null,
      filterFavorites: saved.filterFavorites === true ? true : null,
      filterEvoStage: saved.filterEvoStage ?? null,
      filterGen: saved.filterGen ?? null,
      filterEvolvable: saved.filterEvolvable ?? null,
      filterGmaxDen: saved.filterGmaxDen === true,
      filterHideLevel100: saved.filterHideLevel100 === true,
      showEvoNotifs: true,
    }
  },

  getters: {
    team: (state): OwnedPokemon[] => {
      const bySlot = new Map<number, OwnedPokemon>()
      const candidates = [...state.collection]
        .filter((p) => p.teamSlot !== null && p.teamSlot >= 1 && p.teamSlot <= 6)
        .sort((a, b) => {
          const sa = a.teamSlot ?? 0
          const sb = b.teamSlot ?? 0
          if (sa !== sb) return sa - sb
          // Defensive tie-breaker if corrupted data contains duplicate slot assignments.
          return b.level - a.level || b.stars - a.stars || a.id - b.id
        })

      for (const p of candidates) {
        const slot = p.teamSlot
        if (slot == null || slot < 1 || slot > 6) continue
        if (!bySlot.has(slot)) bySlot.set(slot, p)
      }

      const out: OwnedPokemon[] = []
      for (let slot = 1; slot <= 6; slot++) {
        const p = bySlot.get(slot)
        if (p) out.push(p)
      }
      return out
    },
    teamDps(): number {
      return getTeamPower(this.team)
    },
    collectionCount: (state): number => state.collection.length,
    uniqueSlugs: (state): Set<string> => new Set(state.collection.map((p) => p.slug)),
    maxedSlugs: (state): Set<string> => {
      return new Set(
        state.collection.filter((p) => p.stars >= MAX_STARS).map((p) => p.slug)
      )
    },
    ownedSlugStars: (state): Map<string, number> => {
      const map = new Map<string, number>()
      for (const p of state.collection) {
        const current = map.get(p.slug) ?? 0
        if (p.stars > current) map.set(p.slug, p.stars)
      }
      return map
    },
  },

  actions: {
    newSavedTeamId(): string {
      return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
    },

    normalizeSavedTeam(raw: any): SavedTeam | null {
      if (!raw || typeof raw !== 'object') return null
      const name = String(raw.name ?? '').trim().slice(0, 40)
      if (!name) return null
      const ids = Array.isArray(raw.pokemonIds)
        ? raw.pokemonIds.map((n: any) => Number(n)).filter((n: number) => Number.isInteger(n) && n > 0)
        : []
      if (ids.length === 0) return null
      const tags = Array.isArray(raw.tags)
        ? raw.tags.map((t: any) => String(t ?? '').trim().slice(0, 20)).filter(Boolean).slice(0, 5)
        : []
      const now = Date.now()
      return {
        id: String(raw.id ?? this.newSavedTeamId()),
        name,
        pokemonIds: ids.slice(0, 6),
        tags,
        note: String(raw.note ?? '').trim().slice(0, 140) || '',
        pinned: raw.pinned === true,
        createdAt: Number(raw.createdAt) > 0 ? Number(raw.createdAt) : now,
        updatedAt: Number(raw.updatedAt) > 0 ? Number(raw.updatedAt) : now,
        lastUsedMode: raw.lastUsedMode ? String(raw.lastUsedMode) : null,
      }
    },

    setSavedTeamsFromServer(rawTeams: any[]) {
      const out: SavedTeam[] = []
      for (const raw of rawTeams ?? []) {
        const team = this.normalizeSavedTeam(raw)
        if (team) out.push(team)
      }
      this.savedTeams = out
      resetSavedTeamsDirty()
    },

    resetFiltersToDefault() {
      this.filterSortBy = 'stars'
      this.filterSearch = ''
      this.filterTypes = []
      this.filterRarities = []
      this.filterStars = []
      this.filterShiny = null
      this.filterNewShiny = false
      this.filterTeam = null
      this.filterFavorites = null
      this.filterEvoStage = null
      this.filterGen = null
      this.filterEvolvable = null
      this.filterGmaxDen = false
      this.filterHideLevel100 = false
      this.persistFilters()
    },

    pruneFiltersIfCollectionEmpty() {
      if (this.collection.length > 0) return
      const hasActiveFilters =
        this.filterSearch.length > 0 ||
        this.filterTypes.length > 0 ||
        this.filterRarities.length > 0 ||
        this.filterStars.length > 0 ||
        this.filterShiny != null ||
        this.filterNewShiny ||
        this.filterTeam != null ||
        this.filterFavorites === true ||
        this.filterEvoStage != null ||
        this.filterGen != null ||
        this.filterEvolvable != null ||
        this.filterGmaxDen ||
        this.filterHideLevel100
      if (hasActiveFilters) this.resetFiltersToDefault()
    },

    persistFilters() {
      saveFiltersToStorage({
        filterSortBy: this.filterSortBy,
        filterSearch: this.filterSearch,
        filterTypes: this.filterTypes,
        filterRarities: this.filterRarities,
        filterStars: this.filterStars,
        filterShiny: this.filterShiny,
        filterNewShiny: this.filterNewShiny,
        filterTeam: this.filterTeam,
        filterFavorites: this.filterFavorites,
        filterEvoStage: this.filterEvoStage,
        filterGen: this.filterGen,
        filterEvolvable: this.filterEvolvable,
        filterGmaxDen: this.filterGmaxDen,
        filterHideLevel100: this.filterHideLevel100,
      })
    },

    /** Vide le journal après affichage des toasts (évite une croissance illimitée en session longue). */
    clearEvolutionLog() {
      this.evolutionLog = []
    },

    // Migration: update rarity of existing Pokemon to match current gacha data
    migrateRarities() {
      for (const pokemon of this.collection) {
        // Only update rarity for Pokemon that exist in gacha pools
        // Evolved forms (Ivysaur, Charizard, etc.) are NOT in gacha - keep inherited rarity
        if (!hasKnownRarity(pokemon.slug)) continue
        const currentRarity = getRarity(pokemon.slug)
        if (pokemon.rarity !== currentRarity) {
          pokemon.rarity = currentRarity
        }
      }
    },

    // Migration: Gmax are now unlockable forms on their base Pokémon, not standalone species.
    migrateGmaxForms() {
      const toRemove = new Set<number>()
      let changed = false
      for (const pokemon of this.collection) {
        if (!pokemon.slug.includes('-gmax')) continue
        const legacyFormSlug = pokemon.slug
        const baseSlug = resolvePowerBaseSlug(legacyFormSlug)
        if (!baseSlug || baseSlug === legacyFormSlug) continue

        const baseEntry = POKEDEX.find((entry) => entry.slug === baseSlug)
        const existing = this.collection.find((candidate) =>
          candidate.id !== pokemon.id &&
          candidate.slug === baseSlug &&
          candidate.isShiny === pokemon.isShiny
        )

        if (existing) {
          existing.stars = Math.max(existing.stars, pokemon.stars)
          existing.level = Math.max(existing.level, pokemon.level)
          existing.xp = Math.max(existing.xp, pokemon.xp)
          existing.cosmeticFormSlug = legacyFormSlug
          if (existing.teamSlot === null && pokemon.teamSlot !== null) existing.teamSlot = pokemon.teamSlot
          toRemove.add(pokemon.id)
          changed = true
          continue
        }

        pokemon.slug = baseSlug
        pokemon.nameFr = baseEntry?.nameFr ?? pokemon.nameFr
        pokemon.nameEn = baseEntry?.nameEn ?? pokemon.nameEn
        pokemon.cosmeticFormSlug = legacyFormSlug
        pokemon.rarity = getDisplayRarity({ slug: baseSlug, cosmeticFormSlug: legacyFormSlug, rarity: pokemon.rarity })
        changed = true
      }

      if (toRemove.size > 0) {
        this.collection = this.collection.filter((pokemon) => !toRemove.has(pokemon.id))
      }
      if (changed) {
        markPokemonsDirty()
      }
    },

    // Migration: sync nameFr/nameEn depuis le Pokédex / formes (corrige ex. « zapdos » → Électhor)
    migrateNames() {
      for (const pokemon of this.collection) {
        const canon = resolveCanonicalSpeciesNames(pokemon.slug)
        if (!canon) continue
        if (pokemon.nameFr !== canon.nameFr) pokemon.nameFr = canon.nameFr
        if (pokemon.nameEn !== canon.nameEn) pokemon.nameEn = canon.nameEn
      }
    },

    /**
     * Recalcule le niveau à partir de l’XP cumulée et de la courbe actuelle (`pokemonXpForLevel`).
     * Nécessaire après correction des multiplicateurs de rareté (ex. uber_mega qui retombait sur ×1.0).
     */
    reconcileLevelsToXpCurve() {
      let changed = false
      for (const pokemon of this.collection) {
        const r = pokemon.rarity
        let level = pokemon.level
        const xp = pokemon.xp
        while (level > 1 && xp < pokemonXpForLevel(level, r)) {
          level--
          changed = true
        }
        while (level < MAX_LEVEL && xp >= pokemonXpForLevel(level + 1, r)) {
          level++
          changed = true
        }
        if (level !== pokemon.level) {
          pokemon.level = level
        }
      }
      if (changed) {
        markPokemonsDirty()
      }
    },

    addPokemon(pokemon: Omit<OwnedPokemon, 'id' | 'serverId' | 'level' | 'xp' | 'teamSlot'>): {
      isNew: boolean
      isMaxed: boolean
      wasAlreadyMaxed: boolean
      pokemon: OwnedPokemon
    } {
      const existing = this.collection.find(
        (p) => p.slug === pokemon.slug && p.isShiny === pokemon.isShiny
      )

      if (existing) {
        const wasAlreadyMaxed = existing.stars >= MAX_STARS
        existing.stars = Math.min(existing.stars + 1, MAX_STARS)
        if (pokemon.cosmeticFormSlug) existing.cosmeticFormSlug = pokemon.cosmeticFormSlug
        markPokemonIdDirty(existing.id)
        markPokemonsDirty()
        return { 
          isNew: false, 
          isMaxed: existing.stars >= MAX_STARS, 
          wasAlreadyMaxed,
          pokemon: existing 
        }
      }

      // Find first available team slot (1-6), or null if team is full
      const usedSlots = new Set(this.team.map((p) => p.teamSlot))
      let freeSlot: number | null = null
      for (let s = 1; s <= 6; s++) {
        if (!usedSlots.has(s)) {
          freeSlot = s
          break
        }
      }

      const newPokemon: OwnedPokemon = {
        ...pokemon,
        id: this.nextId++,
        serverId: null,
        level: 1,
        xp: 0,
        teamSlot: freeSlot,
        isFavorite: false,
        cosmeticFormSlug: pokemon.cosmeticFormSlug ?? null,
      }
      this.collection.push(newPokemon)
      markPokemonIdDirty(newPokemon.id)
      markPokemonsDirty()
      return { isNew: true, isMaxed: false, wasAlreadyMaxed: false, pokemon: newPokemon }
    },

    addPokemonBatch(pokemons: AddPokemonInput[]): Array<{
      isNew: boolean
      isMaxed: boolean
      wasAlreadyMaxed: boolean
      pokemon: OwnedPokemon
    }> {
      if (pokemons.length === 0) return []

      const existingByKey = new Map<string, OwnedPokemon>()
      const usedSlots = new Set<number>()

      for (const p of this.collection) {
        existingByKey.set(`${p.slug}:${p.isShiny ? 1 : 0}`, p)
        if (p.teamSlot !== null && p.teamSlot >= 1 && p.teamSlot <= 6) {
          usedSlots.add(p.teamSlot)
        }
      }

      let nextAutoTeamSlot = 1
      const consumeNextFreeSlot = (): number | null => {
        while (nextAutoTeamSlot <= 6) {
          if (!usedSlots.has(nextAutoTeamSlot)) {
            usedSlots.add(nextAutoTeamSlot)
            return nextAutoTeamSlot++
          }
          nextAutoTeamSlot++
        }
        return null
      }

      const out: Array<{
        isNew: boolean
        isMaxed: boolean
        wasAlreadyMaxed: boolean
        pokemon: OwnedPokemon
      }> = []

      for (const pokemon of pokemons) {
        const key = `${pokemon.slug}:${pokemon.isShiny ? 1 : 0}`
        const existing = existingByKey.get(key)
        if (existing) {
          const wasAlreadyMaxed = existing.stars >= MAX_STARS
          existing.stars = Math.min(existing.stars + 1, MAX_STARS)
          if (pokemon.cosmeticFormSlug) existing.cosmeticFormSlug = pokemon.cosmeticFormSlug
          markPokemonIdDirty(existing.id)
          out.push({
            isNew: false,
            isMaxed: existing.stars >= MAX_STARS,
            wasAlreadyMaxed,
            pokemon: existing,
          })
          continue
        }

        const freeSlot = consumeNextFreeSlot()
        const newPokemon: OwnedPokemon = {
          ...pokemon,
          id: this.nextId++,
          serverId: null,
          level: 1,
          xp: 0,
          teamSlot: freeSlot,
          isFavorite: false,
          cosmeticFormSlug: pokemon.cosmeticFormSlug ?? null,
        }
        this.collection.push(newPokemon)
        markPokemonIdDirty(newPokemon.id)
        existingByKey.set(key, newPokemon)
        out.push({
          isNew: true,
          isMaxed: false,
          wasAlreadyMaxed: false,
          pokemon: newPokemon,
        })
      }

      markPokemonsDirty()
      return out
    },

    addPokemonRaw(pokemon: Omit<OwnedPokemon, 'id' | 'serverId' | 'level' | 'xp' | 'teamSlot'>): OwnedPokemon {
      const newPokemon: OwnedPokemon = {
        ...pokemon,
        id: this.nextId++,
        serverId: null,
        level: 1,
        xp: 0,
        teamSlot: null,
        isFavorite: false,
      }
      this.collection.push(newPokemon)
      markPokemonIdDirty(newPokemon.id)
      markPokemonsDirty()
      return newPokemon
    },

    setTeamSlot(pokemonId: number, slot: number | null) {
      const pokemon = this.collection.find((p) => p.id === pokemonId)
      if (!pokemon) return

      if (slot !== null) {
        // Block if team already has 6 members and this pokemon isn't already in team
        if (pokemon.teamSlot === null && this.team.length >= 6) return
        // Ensure slot is within 1-6
        if (slot < 1 || slot > 6) return

        const occupant = this.collection.find((p) => p.teamSlot === slot)
        if (occupant) {
          occupant.teamSlot = pokemon.teamSlot
          markPokemonIdDirty(occupant.id)
        }
      }
      pokemon.teamSlot = slot
      markPokemonIdDirty(pokemonId)
      markPokemonsDirty()
    },

    removeFromTeam(pokemonId: number) {
      const pokemon = this.collection.find((p) => p.id === pokemonId)
      if (pokemon) {
        pokemon.teamSlot = null
        markPokemonIdDirty(pokemonId)
        markPokemonsDirty()
      }
    },

    setFavorite(pokemonId: number, favorite: boolean) {
      const pokemon = this.collection.find((p) => p.id === pokemonId)
      if (!pokemon) return
      const next = favorite === true
      if (Boolean(pokemon.isFavorite) === next) return
      pokemon.isFavorite = next
      // Marquer l'id dirty : sinon un delta save déclenché par un autre Pokémon omet
      // celui-ci et le favori n'est jamais persisté (revert au prochain load / éclosion).
      markPokemonIdDirty(pokemonId)
      markPokemonsDirty()
    },

    setCosmeticForm(pokemonId: number, formSlug: string | null) {
      const pokemon = this.collection.find((p) => p.id === pokemonId)
      if (!pokemon) return
      const next = formSlug || null
      if ((pokemon.cosmeticFormSlug ?? null) === next) return
      if (next) {
        const gmax = useGmaxRaidStore()
        if (!isPokemonFormUnlocked(pokemon.slug, next, gmax.progressBySlug, this.collection)) {
          return
        }
      }
      pokemon.cosmeticFormSlug = next
      markPokemonIdDirty(pokemonId)
      markPokemonsDirty()
    },

    /** Retire les formes Gmax non débloquées (ex. sélection avant capture raid). */
    sanitizeUnownedGmaxForms() {
      const gmax = useGmaxRaidStore()
      let changed = false
      for (const pokemon of this.collection) {
        const form = pokemon.cosmeticFormSlug
        if (!form || !isGmaxFormSlug(form)) continue
        if (isGmaxFormUnlocked(form, gmax.progressBySlug, this.collection)) continue
        pokemon.cosmeticFormSlug = null
        markPokemonIdDirty(pokemon.id)
        changed = true
      }
      if (changed) markPokemonsDirty()
    },

    toggleFavorite(pokemonId: number) {
      const pokemon = this.collection.find((p) => p.id === pokemonId)
      if (!pokemon) return
      pokemon.isFavorite = !Boolean(pokemon.isFavorite)
      // Marquer l'id dirty : sinon un delta save déclenché par un autre Pokémon omet
      // celui-ci et le favori n'est jamais persisté (revert au prochain load / éclosion).
      markPokemonIdDirty(pokemonId)
      markPokemonsDirty()
    },

    addPokemonXp(pokemonId: number, amount: number, currentGeneration?: number) {
      const pokemon = this.collection.find((p) => p.id === pokemonId)
      if (!pokemon || pokemon.level >= MAX_LEVEL) return
      const levelBefore = pokemon.level
      pokemon.xp += amount
      while (pokemon.level < MAX_LEVEL && pokemon.xp >= pokemonXpForLevel(pokemon.level + 1, pokemon.rarity)) {
        pokemon.level++
      }
      markPokemonsDirty()
      markPokemonIdDirty(pokemonId)
      // After all level-ups, check if we crossed any evolution threshold
      this.checkLevelEvolution(pokemon, levelBefore, currentGeneration)
    },

    /**
     * Applique l'état d'entraînement AUTORITAIRE renvoyé par l'endpoint atomique
     * `/shop/feed-candy` (bonbons + XP committés en une transaction serveur).
     * Ne baisse jamais le niveau/XP, puis relance le check d'évolution local
     * (l'évolution reste gérée côté client, hors transaction bonbons).
     */
    applyServerCandyFeed(
      pokemonId: number,
      serverLevel: number,
      serverXp: number,
      currentGeneration?: number,
    ) {
      const pokemon = this.collection.find((p) => p.id === pokemonId)
      if (!pokemon) return
      const levelBefore = pokemon.level
      const nextXp = Math.max(pokemon.xp, Math.max(0, Math.floor(serverXp)))
      const nextLevel = Math.max(
        pokemon.level,
        Math.min(MAX_LEVEL, Math.max(1, Math.floor(serverLevel))),
      )
      if (nextXp === pokemon.xp && nextLevel === pokemon.level) return
      pokemon.xp = nextXp
      pokemon.level = nextLevel
      markPokemonsDirty()
      markPokemonIdDirty(pokemonId)
      this.checkLevelEvolution(pokemon, levelBefore, currentGeneration)
    },

    /** Vérifie et applique une évolution de niveau franchie (living dex). Extrait de `addPokemonXp`. */
    checkLevelEvolution(
      pokemon: OwnedPokemon,
      levelBefore: number,
      currentGeneration?: number,
    ) {
      if (pokemon.level <= levelBefore) return
      const crossed = getLevelEvolutionsCrossed(pokemon.slug, pokemon.level, levelBefore)
      const evo = pickLevelEvolutionForLivingDex(
        crossed,
        pokemon,
        this.collection,
        currentGeneration ?? 9,
      )
      if (evo && evo.levelRequired && levelBefore < evo.levelRequired) {
        const targetKey = `${evo.toSlug}-${pokemon.isShiny}`
        const alreadyHaveTarget = this.collection.some(
          (p) => `${p.slug}-${p.isShiny}` === targetKey
        )
        // Bébés / pré-évos : ne pas dupliquer si la forme finale est déjà au Pokédex (ex. Mime Jr. + M. Mime)
        if (!alreadyHaveTarget) {
          pokemon.hasEvolved = false
          this.applyEvolution(pokemon, evo)
        }
      }
    },

    evolveWithItem(pokemonId: number, itemId: string, currentGeneration?: number): number | null {
      const pokemon = this.collection.find((p) => p.id === pokemonId)
      if (!pokemon) return null
      const evo = canEvolveByItem(pokemon.slug, itemId)
      if (!evo) return null
      const maxGen = currentGeneration ?? 9
      if (getGenForSlug(evo.toSlug) > maxGen) return null
      // Reset hasEvolved so item evolutions always work (living dex: original stays for future stones)
      pokemon.hasEvolved = false
      return this.applyEvolution(pokemon, evo)
    },

    /** Annule une évolution par objet locale (ex. échec réseau avant persistance). */
    revertItemEvolution(sourcePokemonId: number, evolvedLocalId: number): boolean {
      const source = this.collection.find((p) => p.id === sourcePokemonId)
      const evolvedIdx = this.collection.findIndex((p) => p.id === evolvedLocalId)
      if (!source || evolvedIdx < 0) return false
      const evolved = this.collection[evolvedIdx]!
      if (evolved.teamSlot !== null) {
        source.teamSlot = evolved.teamSlot
      }
      source.hasEvolved = false
      this.collection.splice(evolvedIdx, 1)
      markPokemonsDirty()
      markPokemonIdDirty(sourcePokemonId)
      return true
    },

    /** Applique une évolution confirmée côté serveur (endpoint atomique). */
    applyServerItemEvolution(
      sourceLocalId: number,
      payload: {
        source: { serverId: number; teamSlot: number | null }
        evolved: {
          serverId: number
          slug: string
          nameFr: string
          nameEn: string
          level: number
          xp: number
          stars: number
          isShiny: boolean
          rarity: string
          teamSlot: number | null
          isFavorite: boolean
        }
      },
    ): number | null {
      const source = this.collection.find((p) => p.id === sourceLocalId)
      if (!source) return null

      source.serverId = payload.source.serverId
      source.teamSlot = payload.source.teamSlot
      source.hasEvolved = true

      const targetKey = `${payload.evolved.slug}-${payload.evolved.isShiny}`
      const existing = this.collection.find((p) => `${p.slug}-${p.isShiny}` === targetKey)
      if (existing) {
        existing.serverId = payload.evolved.serverId
        existing.teamSlot = payload.evolved.teamSlot
        existing.level = payload.evolved.level
        existing.xp = payload.evolved.xp
        existing.stars = payload.evolved.stars
        existing.rarity = hasKnownRarity(payload.evolved.slug)
          ? getRarity(payload.evolved.slug)
          : (payload.evolved.rarity as Rarity)
        existing.isFavorite = payload.evolved.isFavorite === true
        return existing.id
      }

      const evolved: OwnedPokemon = {
        id: this.nextId++,
        serverId: payload.evolved.serverId,
        slug: payload.evolved.slug,
        nameFr: payload.evolved.nameFr,
        nameEn: payload.evolved.nameEn,
        level: payload.evolved.level,
        xp: payload.evolved.xp,
        stars: payload.evolved.stars,
        isShiny: payload.evolved.isShiny,
        rarity: hasKnownRarity(payload.evolved.slug)
          ? getRarity(payload.evolved.slug)
          : (payload.evolved.rarity as Rarity),
        teamSlot: payload.evolved.teamSlot,
        isFavorite: payload.evolved.isFavorite === true,
        hasEvolved: false,
      }
      this.collection.push(evolved)
      return evolved.id
    },

    evolveAllWithItem(slug: string, itemId: string, currentGeneration?: number): number {
      const evo = canEvolveByItem(slug, itemId)
      if (!evo) return 0
      const maxGen = currentGeneration ?? 9
      if (getGenForSlug(evo.toSlug) > maxGen) return 0
      const targets = this.collection.filter((p) => p.slug === slug)
      for (const p of targets) {
        // Reset hasEvolved so item evolutions always work (living dex: original stays for future stones)
        p.hasEvolved = false
        this.applyEvolution(p, evo)
      }
      return targets.length
    },

    applyEvolution(pokemon: OwnedPokemon, evo: Evolution): number | null {
      const targetKey = `${evo.toSlug}-${pokemon.isShiny}`
      // Ne jamais ajouter un doublon de la forme évoluée (vivant-dex : 1 entrée par slug+shiny)
      if (this.collection.some((p) => `${p.slug}-${p.isShiny}` === targetKey)) return null

      // Mark this pokemon as having evolved to prevent multiple evolutions
      pokemon.hasEvolved = true
      
      const wasInTeam = pokemon.teamSlot !== null
      const slot = pokemon.teamSlot
      
      // Living dex: keep the original pokemon, add the evolution as a new entry at level 1
      const evolved: OwnedPokemon = {
        id: this.nextId++,
        serverId: null,
        slug: evo.toSlug,
        nameFr: evo.toNameFr,
        nameEn: evo.toNameEn,
        level: 1,
        xp: 0,
        stars: 1,
        isShiny: pokemon.isShiny,
        rarity: hasKnownRarity(evo.toSlug) ? getRarity(evo.toSlug) : pokemon.rarity,
        teamSlot: pokemon.teamSlot,
        isFavorite: false,
        hasEvolved: false,
      }
      // Remove original from team, put evolved in its slot
      pokemon.teamSlot = null
      this.collection.push(evolved)
      /** Toujours inclure base + évolué dans le delta save (équipe ∪ dirtyIds) : sinon hors-équipe = payload sans INSERT. */
      markPokemonIdDirty(pokemon.id)
      markPokemonIdDirty(evolved.id)
      markPokemonsDirty()
      
      // Log evolution for toast notifications
      this.evolutionLog.push({
        fromNameFr: pokemon.nameFr,
        fromNameEn: pokemon.nameEn,
        toNameFr: evo.toNameFr,
        toNameEn: evo.toNameEn,
        wasInTeam,
        slot,
      })

      // Cosmovum → légendaire : l'autre version exclusive (Solgaleo / Lunala) rejoint la collection aussi
      // (un seul Cosmovum, pas deux lignes — même shiny).
      if (pokemon.slug === 'cosmoem' && (evo.toSlug === 'solgaleo' || evo.toSlug === 'lunala')) {
        const twinSlug = evo.toSlug === 'solgaleo' ? 'lunala' : 'solgaleo'
        const twinKey = `${twinSlug}-${pokemon.isShiny}`
        if (!this.collection.some((p) => `${p.slug}-${p.isShiny}` === twinKey)) {
          const twinDex = POKEDEX.find((e) => e.slug === twinSlug)
          if (twinDex) {
            this.addPokemonRaw({
              slug: twinSlug,
              nameFr: twinDex.nameFr,
              nameEn: twinDex.nameEn,
              stars: 1,
              isShiny: pokemon.isShiny,
              rarity: hasKnownRarity(twinSlug) ? getRarity(twinSlug) : pokemon.rarity,
              hasEvolved: false,
            })
            this.evolutionLog.push({
              fromNameFr: pokemon.nameFr,
              fromNameEn: pokemon.nameEn,
              toNameFr: twinDex.nameFr,
              toNameEn: twinDex.nameEn,
              wasInTeam: false,
              slot: null,
              toastFr: `${twinDex.nameFr} rejoint aussi ta collection (ligne Cosmog) !`,
              toastEn: `${twinDex.nameEn} also joins your collection (Cosmog line)!`,
            })
          }
        }
      }

      return evolved.id
    },

    // Team management
    saveTeam(name: string, options?: { tags?: string[]; note?: string; pinned?: boolean }) {
      const currentTeam = this.team.map(p => p.id)
      if (currentTeam.length === 0) return
      const now = Date.now()
      const existingIndex = this.savedTeams.findIndex(t => t.name === name)
      if (existingIndex >= 0) {
        const existing = this.savedTeams[existingIndex]
        if (existing) {
          existing.pokemonIds = currentTeam
          existing.updatedAt = now
          if (options?.tags) existing.tags = options.tags.slice(0, 5)
          if (options?.note !== undefined) existing.note = options.note.slice(0, 140)
          if (options?.pinned !== undefined) existing.pinned = options.pinned
        }
      } else {
        this.savedTeams.push({
          id: this.newSavedTeamId(),
          name,
          pokemonIds: currentTeam,
          tags: options?.tags?.slice(0, 5) ?? [],
          note: options?.note?.slice(0, 140) ?? '',
          pinned: options?.pinned === true,
          createdAt: now,
          updatedAt: now,
          lastUsedMode: null,
        })
      }
      markSavedTeamsDirty()
    },

    updateSavedTeamMeta(id: string, patch: { name?: string; tags?: string[]; note?: string; pinned?: boolean }) {
      const team = this.savedTeams.find((t) => t.id === id)
      if (!team) return
      if (patch.name !== undefined) {
        const next = String(patch.name).trim().slice(0, 40)
        if (next) team.name = next
      }
      if (patch.tags !== undefined) team.tags = patch.tags.slice(0, 5)
      if (patch.note !== undefined) team.note = patch.note.slice(0, 140)
      if (patch.pinned !== undefined) team.pinned = patch.pinned
      team.updatedAt = Date.now()
      markSavedTeamsDirty()
    },

    getSavedTeamByName(name: string): SavedTeam | undefined {
      return this.savedTeams.find((t) => t.name === name)
    },

    getSavedTeamById(id: string): SavedTeam | undefined {
      return this.savedTeams.find((t) => t.id === id)
    },

    loadTeam(name: string) {
      const saved = this.savedTeams.find(t => t.name === name)
      if (!saved) return

      const daycare = useDaycareStore()
      
      // Clear current team
      for (const p of this.collection) {
        p.teamSlot = null
      }
      
      // Load saved team — skip pokemon currently in daycare
      let slot = 1
      for (const id of saved.pokemonIds) {
        if (slot > 6) break
        const pokemon = this.collection.find(p => p.id === id)
        if (!pokemon) continue
        if (daycare.hasSlug(pokemon.slug, pokemon.isShiny)) continue
        pokemon.teamSlot = slot++
      }
      for (const p of this.collection) {
        markPokemonIdDirty(p.id)
      }
      markPokemonsDirty()
    },

    applySavedTeam(
      name: string,
      options?: {
        mode?: 'general'
        requiredGeneration?: number | null
        allowAutoFill?: boolean
      }
    ) {
      const saved = this.savedTeams.find(t => t.name === name)
      if (!saved) {
        return { ok: false, loaded: 0, skippedMissing: 0, skippedDaycare: 0, skippedMode: 0, autoFilled: 0 }
      }
      const daycare = useDaycareStore()
      const requiredGen = options?.requiredGeneration ?? null
      const usedIds = new Set<number>()
      let loaded = 0
      let skippedMissing = 0
      let skippedDaycare = 0
      let skippedMode = 0

      for (const p of this.collection) p.teamSlot = null

      for (const id of saved.pokemonIds) {
        if (loaded >= 6) break
        const pokemon = this.collection.find(p => p.id === id)
        if (!pokemon) {
          skippedMissing++
          continue
        }
        if (requiredGen != null && getGenForSlug(pokemon.slug) !== requiredGen) {
          skippedMode++
          continue
        }
        if (daycare.hasSlug(pokemon.slug, pokemon.isShiny)) {
          skippedDaycare++
          continue
        }
        pokemon.teamSlot = loaded + 1
        usedIds.add(pokemon.id)
        loaded++
      }

      let autoFilled = 0
      // Only auto-fill when at least one preset member was actually restored.
      // This prevents loading a random team when the whole preset is unavailable
      // (e.g. all members are in daycare).
      if (options?.allowAutoFill && loaded > 0 && loaded < 6) {
        const candidates = this.collection
          .filter((p) => !usedIds.has(p.id))
          .filter((p) => p.teamSlot === null)
          .filter((p) => (requiredGen == null ? true : getGenForSlug(p.slug) === requiredGen))
          .filter((p) => !daycare.hasSlug(p.slug, p.isShiny))
          .sort((a, b) => b.level - a.level || b.stars - a.stars)
        for (const p of candidates) {
          if (loaded >= 6) break
          p.teamSlot = loaded + 1
          loaded++
          autoFilled++
        }
      }

      saved.lastUsedMode = options?.mode ?? 'general'
      saved.updatedAt = Date.now()
      markSavedTeamsDirty()
      for (const p of this.collection) {
        markPokemonIdDirty(p.id)
      }
      markPokemonsDirty()
      return { ok: loaded > 0, loaded, skippedMissing, skippedDaycare, skippedMode, autoFilled }
    },

    deleteTeam(name: string) {
      this.savedTeams = this.savedTeams.filter(t => t.name !== name)
      markSavedTeamsDirty()
    },

    /** Déplace une équipe sauvegardée d’un cran (pour classer la liste). */
    moveSavedTeam(index: number, delta: number) {
      const to = index + delta
      if (to < 0 || to >= this.savedTeams.length) return
      const list = [...this.savedTeams]
      const [item] = list.splice(index, 1)
      if (!item) return
      list.splice(to, 0, item)
      this.savedTeams = list
      markSavedTeamsDirty()
    },

    // Remove duplicate Pokemon (same slug + isShiny)
    // Keep the one with most stars, then highest level
    removeDuplicates() {
      const seen = new Map<string, OwnedPokemon>()
      const toRemove: number[] = []

      for (const pokemon of this.collection) {
        const key = `${pokemon.slug}-${pokemon.isShiny}`
        const existing = seen.get(key)

        if (!existing) {
          seen.set(key, pokemon)
        } else {
          // Deux lignes DB (même slug+shiny) : garder le meilleur palier — ne pas additionner (3★+2★→5★ abusif).
          const mergedStars = Math.min(MAX_STARS, Math.max(existing.stars, pokemon.stars))
          const keepExisting =
            existing.stars > pokemon.stars ||
            (existing.stars === pokemon.stars && existing.level >= pokemon.level)

          if (keepExisting) {
            existing.stars = mergedStars
            if (!existing.cosmeticFormSlug && pokemon.cosmeticFormSlug) {
              existing.cosmeticFormSlug = pokemon.cosmeticFormSlug
            }
            if (pokemon.teamSlot !== null && existing.teamSlot === null) {
              existing.teamSlot = pokemon.teamSlot
            }
            toRemove.push(pokemon.id)
          } else {
            pokemon.stars = mergedStars
            if (!pokemon.cosmeticFormSlug && existing.cosmeticFormSlug) {
              pokemon.cosmeticFormSlug = existing.cosmeticFormSlug
            }
            if (existing.teamSlot !== null && pokemon.teamSlot === null) {
              pokemon.teamSlot = existing.teamSlot
            }
            toRemove.push(existing.id)
            seen.set(key, pokemon)
          }
        }
      }

      if (toRemove.length > 0) {
        console.log(`[Inventory] Removing ${toRemove.length} duplicate Pokemon:`, toRemove)
        this.collection = this.collection.filter(p => !toRemove.includes(p.id))
        markPokemonsDirty()
      }
    },

    // Rebuild hasEvolved flags after loading from server (not persisted)
    // Match base forms to evolved forms 1:1 (don't mark ALL bases as evolved)
    rebuildHasEvolvedFlags() {
      // Reset all flags first
      for (const p of this.collection) p.hasEvolved = false

      // Count how many of each evolved form exist
      const evolvedCounts = new Map<string, number>()
      for (const p of this.collection) {
        const key = `${p.slug}-${p.isShiny}`
        evolvedCounts.set(key, (evolvedCounts.get(key) ?? 0) + 1)
      }

      // Track how many evolved forms have been "claimed" by base forms
      const claimed = new Map<string, number>()

      for (const pokemon of this.collection) {
        const evos = EVOLUTIONS.filter(e => e.fromSlug === pokemon.slug)
        for (const evo of evos) {
          const key = `${evo.toSlug}-${pokemon.isShiny}`
          const total = evolvedCounts.get(key) ?? 0
          const used = claimed.get(key) ?? 0
          if (total > used) {
            pokemon.hasEvolved = true
            claimed.set(key, used + 1)
            break
          }
        }
      }
    },

    // Check all possible evolutions for all Pokemon
    // Uses target existence check instead of hasEvolved to support multi-evolution (Slowpoke→Slowbro/Slowking)
    checkAllEvolutions(currentGeneration?: number) {
      // Skip if nothing changed since last check (avoids O(n) scan on every route change).
      if (!consumeEvolutionCheckNeeded()) return

      const maxGen = currentGeneration ?? 9
      // Build set of existing slug+shiny combos to avoid duplicates
      const existingKeys = new Set<string>()
      for (const p of this.collection) existingKeys.add(`${p.slug}-${p.isShiny}`)

      for (const pokemon of this.collection) {
        const candidates = getApplicableLevelEvolutionsAtLevel(pokemon.slug, pokemon.level)
        const evo = pickLevelEvolutionForLivingDex(candidates, pokemon, this.collection, maxGen)
        if (!evo || !evo.levelRequired || pokemon.level < evo.levelRequired) continue
        const targetKey = `${evo.toSlug}-${pokemon.isShiny}`
        if (existingKeys.has(targetKey)) continue // Target already exists
        pokemon.hasEvolved = false
        this.applyEvolution(pokemon, evo)
        existingKeys.add(targetKey)
      }
    },

    // Migrate starter evolutions to epic rarity
    migrateStarterRarities() {
      const starterSlugs = new Set([
        // Gen 1
        'bulbasaur', 'ivysaur', 'venusaur',
        'charmander', 'charmeleon', 'charizard',
        'squirtle', 'wartortle', 'blastoise',
        // Gen 2
        'chikorita', 'bayleef', 'meganium',
        'cyndaquil', 'quilava', 'typhlosion',
        'totodile', 'croconaw', 'feraligatr',
        // Gen 3
        'treecko', 'grovyle', 'sceptile',
        'torchic', 'combusken', 'blaziken',
        'mudkip', 'marshtomp', 'swampert',
        // Gen 4
        'turtwig', 'grotle', 'torterra',
        'chimchar', 'monferno', 'infernape',
        'piplup', 'prinplup', 'empoleon',
      ])

      for (const pokemon of this.collection) {
        if (starterSlugs.has(pokemon.slug) && pokemon.rarity !== 'epic') {
          pokemon.rarity = 'epic'
        }
      }
    },

    // Migrate Gen 4 evolutions to correct rarity based on their base form
    migrateGen4Evolutions() {
      // Map: evolution slug → correct rarity based on base form
      const evolutionRarities: Record<string, 'common' | 'rare' | 'epic'> = {
        // Budew → Roselia → Roserade (common line)
        'roserade': 'common',
        // Burmy → Wormadam/Mothim (common)
        'wormadam': 'common',
        'mothim': 'common',
        // Aipom → Ambipom (common)
        'ambipom': 'common',
        // Sneasel → Weavile (rare)
        'weavile': 'rare',
        // Magneton → Magnezone (rare - from Magnemite common)
        'magnezone': 'rare',
        // Lickitung → Lickilicky (rare)
        'lickilicky': 'rare',
        // Rhydon → Rhyperior (rare)
        'rhyperior': 'rare',
        // Tangela → Tangrowth (rare)
        'tangrowth': 'rare',
        // Electabuzz → Electivire (rare)
        'electivire': 'rare',
        // Magmar → Magmortar (rare)
        'magmortar': 'rare',
        // Togetic → Togekiss — même ligne que Togepi (common au gacha), pas une rareté épique
        'togekiss': 'common',
        // Yanma → Yanmega (common)
        'yanmega': 'common',
        // Eevee → Glaceon/Leafeon (epic)
        'glaceon': 'epic',
        'leafeon': 'epic',
        // Gligar → Gliscor (rare)
        'gliscor': 'rare',
        // Piloswine → Mamoswine (common from Swinub)
        'mamoswine': 'common',
        // Porygon2 → Porygon-Z (rare)
        'porygonz': 'rare',
        // Kirlia → Gallade (rare from Ralts)
        'gallade': 'rare',
        // Mime Jr. → Mr. Mime (common)
        'mrmime': 'rare', // Actually Mr. Mime is rare in gacha
        // Dusclops → Dusknoir (rare from Duskull)
        'dusknoir': 'rare',
        // Snorunt → Froslass (common)
        'froslass': 'common',
      }

      for (const pokemon of this.collection) {
        const correctRarity = evolutionRarities[pokemon.slug]
        if (correctRarity && pokemon.rarity !== correctRarity) {
          pokemon.rarity = correctRarity
        }
      }
    },
  },
})
